import { useState, useCallback, useMemo, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { message } from "antd";
import { API_BASE_URL } from "../../constants/config";
import { getAndSanitize } from "../utils/sanitization";
import { useRequestDetailsStore } from "../stores/useRequestDetailsStore";
import { usePanelsStore } from "../stores/usePanelsStore";
import { useUserPanel } from "../../contexts/UserPanelContext";
import { useTranslation } from "react-i18next";
import type { TransferOption } from "../../pages/RequestsPanel/types";

interface PaginatedTransfersResponse {
  success: boolean;
  data: TransferOption[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

interface ExtractedOCRData {
  amount: number;
  coelsaId: string;
  sourcePlatform: string;
  operationNumber: string;
  dateTime: Date | null;
}

interface TransferSearchParams {
  identifier: string;
  amount: string;
  dateTime: string;
}

interface TransferPagination {
  current: number;
  pageSize: number;
  total: number;
}

export function useRequestValidation(requestId: string | undefined) {
  const { currentPanelId } = useUserPanel();
  const { t } = useTranslation();

  const [fullRequestData, setFullRequestData] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [processingRequestIds, setProcessingRequestIds] = useState<string[]>([]);

  // Transfer search state
  const [searchParams, setSearchParams] = useState<TransferSearchParams>({
    identifier: "",
    amount: "",
    dateTime: "",
  });
  const [transferPagination, setTransferPagination] = useState<TransferPagination>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch request details
  useEffect(() => {
    const loadRequestDetails = async () => {
      if (!requestId) {
        setFullRequestData(null);
        return;
      }

      setIsLoadingDetails(true);

      try {
        const token = getAndSanitize("token");
        if (!token) {
          setFullRequestData(null);
          return;
        }

        const adminId =
          currentPanelId ||
          getAndSanitize("adminId") ||
          getAndSanitize("userId");

        if (!adminId) {
          setFullRequestData(null);
          return;
        }

        const detailsData = await useRequestDetailsStore
          .getState()
          .fetchDetails(requestId, adminId, { forceRefresh: true });

        if (detailsData) {
          console.log('[useRequestValidation] detailsData recibido:', {
            receipt: detailsData.receipt,
            operationReceipt: detailsData.operation?.receipt,
            hasOperation: !!detailsData.operation,
            status: detailsData.status,
          });
          setFullRequestData({
            ...detailsData,
            requestData: {
              ...detailsData.requestData,
              processingSteps: detailsData.requestData?.processingSteps || [],
            },
          });
        } else {
          console.warn('[useRequestValidation] detailsData es null/undefined');
        }
      } catch (error) {
        console.error("[useRequestValidation] Error loading details:", error);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    loadRequestDetails();
  }, [requestId, currentPanelId]);

  // Paginated transfers query
  const {
    data: paginatedTransfers = { data: [], total: 0 },
    isLoading: isLoadingTransfers,
  } = useQuery<PaginatedTransfersResponse>({
    queryKey: [
      "validation-transfers",
      currentPanelId,
      transferPagination.current,
      transferPagination.pageSize,
      searchParams,
    ],
    queryFn: async () => {
      const token = getAndSanitize("token");
      if (!token) throw new Error("No token available");

      let panelIdParam = currentPanelId || "";
      if (!panelIdParam) {
        try {
          const decodedToken = JSON.parse(atob(token.split(".")[1]));
          panelIdParam = decodedToken.userId;
        } catch {}
      }

      const params: Record<string, string | number> = {
        page: transferPagination.current,
        limit: transferPagination.pageSize,
        panelId: panelIdParam,
      };

      if (searchParams.identifier) params.searchTerm = searchParams.identifier;
      if (searchParams.amount) params.amount = searchParams.amount;
      if (searchParams.dateTime) params.dateTime = searchParams.dateTime;

      const response = await axios.get<PaginatedTransfersResponse>(
        `${API_BASE_URL}/api/transfers/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            "x-admin-id": panelIdParam,
          },
          params,
        }
      );

      return response.data;
    },
    enabled: !!requestId,
    placeholderData: (prev) => prev,
    retry: 1,
    staleTime: 30000,
  });

  // Extract OCR data from processing steps
  const extractedOCRData = useMemo((): ExtractedOCRData | null => {
    if (!fullRequestData?.requestData?.processingSteps) return null;

    const validationStep = fullRequestData.requestData.processingSteps.find(
      (step: any) =>
        step.step === "VALIDATION_ANALYSIS" && step.metadata?.extractedFields
    );

    if (!validationStep) return null;

    const { extractedFields, detectedPlatform } = validationStep.metadata;

    const parseDateTime = (): Date | null => {
      try {
        // Standardized field from backend (ISO 8601)
        if (extractedFields?.normalizedDateTime) {
          const d = new Date(extractedFields.normalizedDateTime);
          if (!isNaN(d.getTime())) return d;
        }

        // Fallback: legacy parsing for old data without normalizedDateTime
        if (extractedFields?.date && extractedFields?.time) {
          let dateStr = `${extractedFields.date} ${extractedFields.time}`;

          if (dateStr.includes(" de ")) {
            const months: Record<string, string> = {
              enero: "01", febrero: "02", marzo: "03",
              abril: "04", mayo: "05", junio: "06",
              julio: "07", agosto: "08", septiembre: "09",
              octubre: "10", noviembre: "11", diciembre: "12",
            };

            const spanishDateRegex =
              /(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})\s+(\d{2}:\d{2})/;
            const match = dateStr.match(spanishDateRegex);

            if (match) {
              const [, day, monthName, year, time] = match;
              const monthNumber = months[monthName.toLowerCase()];
              if (monthNumber) {
                const isoDateStr = `${year}-${monthNumber}-${day.padStart(2, "0")} ${time}:00`;
                const parsed = new Date(isoDateStr);
                return !isNaN(parsed.getTime()) ? parsed : null;
              }
            }

            for (const [spanish, english] of Object.entries({
              enero: "January", febrero: "February", marzo: "March",
              abril: "April", mayo: "May", junio: "June",
              julio: "July", agosto: "August", septiembre: "September",
              octubre: "October", noviembre: "November", diciembre: "December",
            })) {
              dateStr = dateStr.replace(spanish, english);
            }
            dateStr = dateStr.replace(/ de /g, " ");
          }

          const parsed = new Date(dateStr);
          return !isNaN(parsed.getTime()) ? parsed : null;
        } else if (extractedFields?.date) {
          let dateStr = extractedFields.date;

          if (dateStr.includes(" de ")) {
            const months: Record<string, string> = {
              enero: "01", febrero: "02", marzo: "03",
              abril: "04", mayo: "05", junio: "06",
              julio: "07", agosto: "08", septiembre: "09",
              octubre: "10", noviembre: "11", diciembre: "12",
            };

            const spanishDateRegex =
              /(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})/;
            const match = dateStr.match(spanishDateRegex);

            if (match) {
              const [, day, monthName, year] = match;
              const monthNumber = months[monthName.toLowerCase()];
              if (monthNumber) {
                const isoDateStr = `${year}-${monthNumber}-${day.padStart(2, "0")}`;
                const parsed = new Date(isoDateStr);
                return !isNaN(parsed.getTime()) ? parsed : null;
              }
            }
          }

          const parsed = new Date(dateStr);
          return !isNaN(parsed.getTime()) ? parsed : null;
        }
        return null;
      } catch {
        return null;
      }
    };

    return {
      amount: extractedFields?.amount || 0,
      coelsaId: extractedFields?.coelsaId || "",
      sourcePlatform: detectedPlatform || "mercadopago",
      operationNumber: extractedFields?.operationNumber || "",
      dateTime: parseDateTime(),
    };
  }, [fullRequestData]);

  // Resolve panel ID with fallback
  const resolvePanelId = useCallback(async (): Promise<string | null> => {
    if (currentPanelId) return currentPanelId;

    const savedPanelId = getAndSanitize("panelId");
    if (savedPanelId) return savedPanelId;

    try {
      const panels = await usePanelsStore.getState().fetchPanels(true);
      if (panels.length === 1) return panels[0].id;
      if (panels.length > 1) {
        message.error("Seleccione un panel para continuar");
        return null;
      }
      message.error("No hay paneles disponibles");
      return null;
    } catch {
      message.error("Error al obtener paneles");
      return null;
    }
  }, [currentPanelId]);

  // Process mutation (with registered transfer)
  const processMutation = useMutation({
    mutationFn: async (values: {
      requestId: string;
      amount: number;
      bonus: number;
      notify: boolean;
      processDeposit: boolean;
      transferId?: number;
      panelId: string;
      isUnregisteredTransfer?: boolean;
    }) => {
      const token = getAndSanitize("token");
      if (!token) throw new Error("Authentication token is required");

      setProcessingRequestIds((prev) => [...prev, values.requestId]);

      const response = await axios.post(
        `${API_BASE_URL}/api/requests/load-requests/reprocess`,
        { ...values, token },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      useRequestDetailsStore.getState().invalidateCache(variables.requestId);
      await new Promise((resolve) => setTimeout(resolve, 600));
      message.success(t("requestsPanel.notifications.success.requestProcessed"));
      setProcessingRequestIds((prev) =>
        prev.filter((id) => id !== variables.requestId)
      );
    },
    onError: (error: any, variables) => {
      console.error("[useRequestValidation] Process failed:", error);
      message.error(error.response?.data?.message || error.message);
      setProcessingRequestIds((prev) =>
        prev.filter((id) => id !== variables.requestId)
      );
    },
  });

  // Register unregistered transfer mutation
  const registerUnregisteredMutation = useMutation({
    mutationFn: async (values: {
      requestId: string;
      dateTime: string;
      amount: number;
      coelsaId: string;
      sourcePlatform: string;
      bonus: number;
      notify: boolean;
      processDeposit: boolean;
      panelId: string;
    }) => {
      const token = getAndSanitize("token");
      if (!token) throw new Error("Authentication token is required");

      setProcessingRequestIds((prev) => [...prev, values.requestId]);

      const response = await axios.post(
        `${API_BASE_URL}/api/transfers/register-unregistered`,
        {
          ...values,
          isUnregisteredTransfer: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      useRequestDetailsStore.getState().invalidateCache(variables.requestId);
      await new Promise((resolve) => setTimeout(resolve, 600));
      message.success("Transfer registered and request processed successfully");
      setProcessingRequestIds((prev) =>
        prev.filter((id) => id !== variables.requestId)
      );
    },
    onError: (error: any, variables) => {
      console.error("[useRequestValidation] Unregistered submit failed:", error);
      message.error(
        error.response?.data?.message || "Failed to process unregistered transfer"
      );
      setProcessingRequestIds((prev) =>
        prev.filter((id) => id !== variables.requestId)
      );
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async (values: {
      requestId: string;
      rejectionReason: string;
    }) => {
      setProcessingRequestIds((prev) => [...prev, values.requestId]);

      const response = await axios.post(
        `${API_BASE_URL}/api/requests/load-requests/reject`,
        values,
        {
          headers: {
            Authorization: `Bearer ${getAndSanitize("token")}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: async (_, variables) => {
      await new Promise((resolve) => setTimeout(resolve, 600));
      message.success(t("requestsPanel.notifications.success.requestRejected"));
      setProcessingRequestIds((prev) =>
        prev.filter((id) => id !== variables.requestId)
      );
    },
    onError: (error: any, variables) => {
      console.error("[useRequestValidation] Reject failed:", error);
      message.error(
        error.response?.data?.message ||
          t("requestsPanel.notifications.error.rejectError")
      );
      setProcessingRequestIds((prev) =>
        prev.filter((id) => id !== variables.requestId)
      );
    },
  });

  return {
    // Data
    fullRequestData,
    isLoadingDetails,
    extractedOCRData,
    paginatedTransfers,
    isLoadingTransfers,
    currentPanelId,
    processingRequestIds,

    // Transfer search
    searchParams,
    setSearchParams,
    transferPagination,
    setTransferPagination,

    // Mutations
    processMutation,
    registerUnregisteredMutation,
    rejectMutation,

    // Helpers
    resolvePanelId,
  };
}
